import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { Grid } from '@mui/material';
import TextFieldContext from 'components/CustomInputsInFormikContext/TextField';
import CardWrapper from 'components/Financial/CardWrapper';
import { FormikProvider, useFormik } from 'formik';
import DatePickerContext from 'components/CustomInputsInFormikContext/DatePicker';
import NumberFormatContext from 'components/CustomInputsInFormikContext/NumberFormat';
import AutoCompleteContext from 'components/CustomInputsInFormikContext/AutoComplete';
import { calculationStrategy } from 'pages/sale/helpers';
import HandleProductions from 'pages/harvest/handleProductions';
import { useDependencyInjection } from 'hooks/useDependencyInjection';
import { useLoadDataByLocation } from 'hooks/useLoadDataByLocation';
import { saleSchema } from 'pages/sale/schemas';
import { labelField } from 'utils';
import { useHistory } from 'react-router-dom';

function SalesData({ updateTotalPrice, callback = () => {} }, ref) {
  const history = useHistory();

  const date = new Date();
  const status = 'concluída';

  const formik = useFormik({
    initialValues: {
      stakeholder: null,
      date,
      status,
      quantity: '',
      price: '',
      unit_price: '',
      discount: 0,
      description: '',
      harvest: {
        cycles: [],
        date,
        status,
        id: null,
      },
    },
    validationSchema: saleSchema,
  });

  const { api, id } = useDependencyInjection();
  const [clients, setClients] = useState([]);
  const [alter, setAlter] = useState(true);
  const [pondsIsNull, setPondsIsNull] = useState(false);

  const cycles = formik.values.harvest?.cycles ?? [];

  const getValueTotal = formik.values.price;

  /**
   * Função que verifica se uma venda tem produção. Caso não tenha, foi inserido no objeto valores iniciais para que o usuário consiga realizar despesca.
   * @param {*} data
   * @returns
   */
  const validateHarvest = (data) => {
    const { bill, ...rest } = data;
    const payload = !rest.harvest
      ? {
          ...rest,
          harvest: formik.initialValues.harvest,
        }
      : {
          ...rest,
        };

    return payload;
  };

  function setData(data) {
    if (!data) return;
    const payload = validateHarvest(data);

    formik.setValues({
      ...payload,
      discount: payload.discout ?? 0,
    });
  }

  const { harvestId, data } = useLoadDataByLocation({
    setData,
    locationKey: 'harvestId',
  });

  const idSale = data?.id;
  const idHarvest = data?.harvest?.id;

  const handleNavigateAddNewCustomer = () => {
    history.replace('/client', { data: { sales: true, harvestId: harvestId } });
  };

  useEffect(() => {
    async function loadClients() {
      try {
        const response = await api.get('/clients');
        setClients(response.data.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (error) {
        throw new Error('Erro ao buscar clientes');
      }
    }

    loadClients();
  }, [api]);

  useEffect(() => {
    if (getValueTotal > 0) {
      updateTotalPrice(getValueTotal);
    }
  }, [getValueTotal, updateTotalPrice]);

  const handleSubmit = async () => {
    try {
      await formik.submitForm();
    } catch (e) {}
  };

  useEffect(() => {
    callback({
      isValid: formik.isValid,
      values: {
        ...formik.values,
        harvest: {
          ...formik.values.harvest,
          id: harvestId ?? formik.values.harvest?.id,
        },
      },
    });
  }, [callback, formik.isValid, formik.values, harvestId]);

  // Permite que o componente pai chame o método handleSubmit
  useImperativeHandle(ref, () => ({
    handleSubmit,
  }));

  function handleChange(field) {
    // eslint-disable-next-line func-names
    return function (floatValue) {
      formik.setValues({
        ...formik.values,
        ...calculationStrategy({
          ...formik.values,
          [field]: floatValue,
          strategy: field,
        }),
      });
    };
  }

  const calculateQuantity = (cyclesParam) =>
    cyclesParam?.reduce((acc, curr) => acc + curr.production, 0);
  const isPending = formik.values.status.includes('pendente');

  const updateCycles = (cyclesParam) => {
    const totalQuantity = calculateQuantity(cyclesParam);

    formik.setFieldValue('quantity', totalQuantity);
    if (totalQuantity === 0) {
      formik.setFieldValue('discount', 0);
    }
    formik.setFieldValue('harvest.cycles', cyclesParam);
  };

  return (
    <FormikProvider value={formik}>
      <CardWrapper title="Dados da Venda" customInfo={id && `n° ${idSale}`}>
        <Grid item xs={12} sm={6}>
          <AutoCompleteContext
            options={clients}
            label="Cliente"
            name="stakeholder"
            getOptionLabel={(option) => option.name}
            handleNavigateAddNewCustomer={handleNavigateAddNewCustomer}
            typeData
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DatePickerContext label="Data da Venda" name="date" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextFieldContext label="Descrição (Opcional)" name="description" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <HandleProductions
            disabled={isPending}
            harvestId={idHarvest}
            att={setAlter}
            alter={alter}
            supply_id={id}
            temporaryDisabled={false}
            options={cycles}
            callback={updateCycles}
            validateThatPriceHasBeenFilled={() => {
              const totalQuantity = calculateQuantity(cycles);

              if (cycles?.length > 0) {
                formik.setFieldValue('quantity', totalQuantity);
              }

              formik.setFieldTouched('price', true);
              formik.setFieldTouched('harvest.cycles', true);
            }}
            pondsIsNull={pondsIsNull}
            setPondsIsNull={setPondsIsNull}
            error={
              formik.touched.harvest?.cycles && formik.errors.harvest?.cycles
            }
            helperText={
              formik.touched.harvest?.cycles && formik.errors.harvest?.cycles
            }
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <NumberFormatContext
            label={labelField('Quantidade')}
            name="quantity"
            fieldSize={11}
            onValueChangeCallback={handleChange('quantity')}
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <NumberFormatContext
            prefix="R$ "
            decimalScale={2}
            label={labelField('Preço unitário (R$/kg)')}
            name="unit_price"
            onValueChangeCallback={handleChange('unit_price')}
            disabled={
              formik.values.discount ||
              !formik.values.quantity ||
              formik.values.quantity <= 0
            }
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <NumberFormatContext
            prefix="R$ "
            decimalScale={2}
            label="Desconto (R$)"
            name="discount"
            fieldSize={formik.values.price?.length || 0 + 40}
            onValueChangeCallback={handleChange('discount')}
            isAllowed={(values) => values.floatValue < formik.values.price}
            disabled={!formik.values.quantity || formik.values.quantity <= 0}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <NumberFormatContext
            prefix="R$ "
            decimalScale={2}
            label={labelField('Total da Venda (R$)')}
            name="price"
            onValueChangeCallback={handleChange('price')}
            disabled={formik.values.unit_price && formik.values.discount}
          />
        </Grid>
      </CardWrapper>
    </FormikProvider>
  );
}

export default forwardRef(SalesData);
